import Chart from 'chart.js/auto'
import type { Context } from 'chartjs-plugin-datalabels'
import _ from 'lodash'
import pluralize from 'pluralize'
import React from 'react'
import { toast } from 'react-toastify'

import { handleException } from 'clients/exception-handling'
import { DRIVER_EXPORT_LIMIT } from 'features/charts/features/export/constants'
import { ThemeDrivers } from 'features/charts/features/export/types'
import {
  BarChart,
  BubbleChart,
  ChartLegend,
  StackBarChart,
  UnionChart,
  ValuesChart,
} from 'features/charts/types'
import type { DashboardControlsConfig } from 'features/dashboard/types'
import { loadHtml2Canvas } from 'utils/load-html2canvas'

// =============================================================================
// Type Definitions for Chart Data
// =============================================================================

interface TreemapDataItem {
  title: string
  color: string
  value?: number
  children?: TreemapDataItem[]
}

export interface ChartValueItem {
  category: string
  volume?: number
  sentiment?: number
  x_value?: string | number
  y_value?: string | number
  z_value?: string | number
  label?: string
  value?: number
  color?: string
  hidden?: boolean
  x_value_time?: number
  rating?: number
  hover?: Record<string, string | number> | null
  stacked_categories?: StackedCategory[]
  [key: string]:
    | string
    | number
    | boolean
    | null
    | undefined
    | StackedCategory[]
    | Record<string, string | number>
}

export interface StackedCategory {
  category: string
  value: number
  [key: string]: string | number
}

interface ShadedZone {
  visible: boolean
  [key: string]: string | number | boolean
}

export interface ChartWithValues {
  id?: string
  msg?: string
  chart_type: string
  chart_id: string
  title: string
  info?: string
  other_charts?: Array<{ chart_id: string; title: string }>
  values: ChartValueItem[]
  parent_values?: ChartValueItem[]
  category_mapping?: Record<string, string[]>
  x_key: string
  y_key: string
  z_key?: string
  x_title?: string
  y_title?: string
  z_title?: string
  shaded_zones?: ShadedZone[]
  min_value?: number
  max_value?: number
  orientation?: string
  legend_title?: string
  legend_key?: string
  legend_order?: string
  interval?: string
  area?: boolean
  isAlignScales?: boolean
  col_keys?: string[]
  col_titles?: string[]
  avg_value?: number
  overlay_min_value?: number
  overlay_max_value?: number
  min_x?: number
  max_x?: number
  min_y?: number
  max_y?: number
  min_z?: number
  max_z?: number
  [key: string]: unknown
}

const DEFAULT_CATEGORY_KEY = 'category'

export enum ChartTypes {
  BAR = 'bar_charts',
  BUBBLE = 'bubble_charts',
  LINE = 'line_charts',
  STACK_BAR = 'stack_bar_charts',
  PIE = 'pie_charts',
  NUMERICAL = 'numerical_charts',
  TABLE = 'table_charts',
  MATRIX = 'matrix_charts',
}

export const matrixChartIds = [
  '9_3_18_rating',
  '9_3_19_sentiment',
  '9_3_40_rating',
  '9_3_41_sentiment',
  '5_3_18_rating',
  '5_3_19_sentiment',
  '6_3_40_rating',
  '6_3_41_sentiment',
]

export function isValuableChart(chart: UnionChart): chart is ValuesChart {
  return (chart as ValuesChart).values !== undefined
}

export function areLabelsDisplayed(chart: UnionChart): boolean {
  if ((chart as StackBarChart).orientation === 'horizontal') {
    return (chart as StackBarChart).values.length < 20
  } else if ((chart as StackBarChart).orientation === 'vertical') {
    return (chart as StackBarChart).values.length < 12
  } else {
    return true
  }
}

export const highlightHoveredData = (
  chartAPI: Chart | null,
  currentChart: UnionChart | ChartWithValues,
  highlightedLegendItems: ChartLegend[],
  treeView: boolean,
): void => {
  const chartType = currentChart?.chart_type
  if (chartAPI?.data?.datasets) {
    if (chartType === ChartTypes.BUBBLE || chartType === ChartTypes.LINE) {
      const datasets = chartAPI.data.datasets
      if (highlightedLegendItems.length) {
        const selectedItems = datasets.filter((item) =>
          highlightedLegendItems
            .map((el) => el.label)
            .includes(item.label as string),
        )
        const selectedIndexes: number[] = datasets.reduce(function (
          a: number[],
          e,
          i,
        ) {
          if (
            highlightedLegendItems
              .map((el) => el.label)
              .includes(e.label as string)
          )
            a.push(i)
          return a
        }, [])
        if (selectedItems.length) {
          datasets.forEach((item) => {
            if ((item.label as string)?.includes('Outlier Bound')) return
            if (
              !selectedItems
                .map((el) => el.label)
                .includes(item.label as string)
            ) {
              const offset = (item.backgroundColor as string).includes(', 0.25')
                ? 7
                : (item.backgroundColor as string).includes(', 0.1')
                  ? 6
                  : 1
              item.backgroundColor =
                (item.backgroundColor as string).slice(0, -offset) + ', 0.1)'
              if (typeof item.borderColor === 'string') {
                const borderOffset = (item.borderColor as string).includes(
                  ', 0.1',
                )
                  ? 6
                  : 1
                item.borderColor =
                  (item.borderColor as string).slice(0, -borderOffset) +
                  ', 0.1)'
              }
            } else {
              if (typeof item.borderColor === 'string') {
                const offset = (item.backgroundColor as string).includes(
                  ', 0.25',
                )
                  ? 7
                  : (item.backgroundColor as string).includes(', 0.1')
                    ? 6
                    : 1
                item.backgroundColor =
                  (item.backgroundColor as string).slice(0, -offset) + ', 0.25)'
                const borderOffset = (item.borderColor as string).includes(
                  ', 0.1',
                )
                  ? 6
                  : 1
                item.borderColor =
                  (item.borderColor as string).slice(0, -borderOffset) + ')'
              }
            }
            if (selectedIndexes.length) {
              try {
                const datalabels = chartAPI?.options?.plugins?.datalabels
                if (datalabels) {
                  datalabels.color = (context: Context) => {
                    if (selectedIndexes.includes(context.datasetIndex)) {
                      return 'black'
                    }
                    return 'lightgrey'
                  }
                }
              } catch (cause) {
                handleException(cause, {
                  operation: 'chart.selection.highlight',
                })

                /* Chart.js proxy may not be ready */
              }
            }
          })
        }
      } else {
        datasets.forEach((item) => {
          if (item.backgroundColor?.toString().includes(', 0.1')) {
            item.backgroundColor =
              item.backgroundColor.toString().slice(0, -6) + ', 0.25)'
          }
          if (item.borderColor?.toString().includes(', 0.1')) {
            item.borderColor = item.borderColor.toString().slice(0, -6) + ')'
          }
        })
        try {
          const datalabelsReset = chartAPI?.options?.plugins?.datalabels
          if (datalabelsReset) {
            datalabelsReset.color = 'black'
          }
        } catch (cause) {
          handleException(cause, { operation: 'chart.selection.reset' })

          /* Chart.js proxy may not be ready */
        }
      }
    }
    if (chartType === ChartTypes.STACK_BAR) {
      const datasets = chartAPI.data.datasets
      if (highlightedLegendItems.length) {
        const selectedItems = datasets.filter((item) =>
          highlightedLegendItems
            .map((el) => el.label)
            .includes(item.label as string),
        )
        if (selectedItems.length) {
          datasets.forEach((item) => {
            if (!selectedItems.map((el) => el.label).includes(item.label)) {
              const offset = (item.backgroundColor as string).includes(', 0.1')
                ? 6
                : 1
              item.backgroundColor =
                item.backgroundColor?.toString().slice(0, -offset) + ', 0.1)'
            } else {
              if (item.backgroundColor?.toString().includes(', 0.1')) {
                item.backgroundColor =
                  item.backgroundColor.toString().slice(0, -6) + ')'
              }
            }
          })
        }
      } else {
        datasets.forEach((item) => {
          if (item.backgroundColor?.toString().includes(', 0.1')) {
            item.backgroundColor =
              item.backgroundColor.toString().slice(0, -6) + ')'
          }
        })
      }
    }
    if (chartType === ChartTypes.BAR) {
      const datasets = chartAPI.data.datasets[1]
      if (datasets && highlightedLegendItems.length) {
        datasets.backgroundColor = (datasets.backgroundColor as string[]).map(
          (item) => {
            if (!item) return item
            const offset = item.includes(', 0.1') ? 6 : 1
            const newItem = !highlightedLegendItems
              .map((el) => el.color)
              .includes(item.slice(0, -offset) + ')')
              ? item.slice(0, -offset) + ', 0.1)'
              : item.slice(0, -offset) + ')'
            return newItem
          },
        )
      } else {
        if (!!datasets && typeof datasets?.backgroundColor !== 'string') {
          datasets.backgroundColor = (
            datasets.backgroundColor as string[]
          )?.map((item) =>
            item?.includes(', 0.1') ? (item = item.slice(0, -6) + ')') : item,
          )
        }
      }
    }
    if (chartType === ChartTypes.PIE && !treeView) {
      const datasets = chartAPI.data.datasets[0]
      if (!datasets) {
        // no-op: skip highlight logic when datasets missing
      } else if (highlightedLegendItems.length) {
        const highlightedLabels = highlightedLegendItems.map((el) => el.label)
        chartAPI.data.labels?.forEach((el, index) => {
          if (highlightedLabels.includes(el as string)) {
            const item = (datasets.backgroundColor as string[])?.[index]
            if (!item) return
            const offset = item.includes(', 0.1') ? 6 : 1
            ;(datasets.backgroundColor as string[])[index] =
              item.slice(0, -offset) + ')'
          } else {
            const item = (datasets.backgroundColor as string[])?.[index]
            if (!item) return
            const offset = item.includes(', 0.1') ? 6 : 1
            ;(datasets.backgroundColor as string[])[index] =
              item.slice(0, -offset) + ', 0.1)'
          }
        })
      } else {
        try {
          datasets.backgroundColor = (datasets.backgroundColor as string[]).map(
            (item) =>
              item.includes(', 0.1') ? (item = item.slice(0, -6) + ')') : item,
          )
        } catch (_e) {
          handleException(_e, { operation: 'chart.colors.restore' })

          /* intentionally empty */
        }
      }
    }
    if (chartType === ChartTypes.PIE && treeView) {
      const datasets = (
        chartAPI.data.datasets[0] as unknown as Record<string, unknown>
      )?.['tree'] as TreemapDataItem[] | undefined
      if (datasets) {
        if (highlightedLegendItems.length) {
          // TODO new typescript broke this EFE-97
          const selectedItems = datasets.filter((item: TreemapDataItem) =>
            highlightedLegendItems.map((el) => el.label).includes(item.title),
          )
          if (selectedItems.length) {
            datasets.forEach((item: TreemapDataItem) => {
              if (!item?.color) return
              if (
                !selectedItems
                  .map((el: TreemapDataItem) => el.title)
                  .includes(item.title)
              ) {
                const offset = item.color.toString().includes(', 0.1') ? 6 : 1
                item.color = item.color.toString().slice(0, -offset) + ', 0.1)'
              } else {
                if (item.color.toString().includes(', 0.1')) {
                  item.color = item.color.toString().slice(0, -6) + ')'
                }
              }
            })
          } else {
            datasets.forEach((item: TreemapDataItem) => {
              if (!item?.color) return
              if (item.color.toString().includes(', 0.1')) {
                item.color = item.color.toString().slice(0, -6) + ')'
              }
            })
          }
        } else {
          datasets.forEach((item: TreemapDataItem) => {
            if (!item?.color) return
            if (item.color.toString().includes(', 0.1')) {
              item.color = item.color.toString().slice(0, -6) + ')'
            }
          })
        }
      }
    }
    if (chartType === ChartTypes.MATRIX) {
      const datasets = chartAPI.data.datasets[0]?.data as unknown as
        | ChartValueItem[]
        | undefined
      if (!datasets) {
        // no-op: skip highlight logic when datasets missing
      } else if (highlightedLegendItems.length) {
        const selectedItems = datasets.filter((item: ChartValueItem) =>
          highlightedLegendItems
            .map((el) => el.color)
            .includes(item.color ?? ''),
        )
        if (selectedItems.length) {
          datasets.forEach((item: ChartValueItem) => {
            if (!item?.color) return
            if (
              !selectedItems
                .map((el: ChartValueItem) => el.color)
                .includes(item.color)
            ) {
              const offset = item.color.toString().includes(', 0.1') ? 6 : 1
              item.color = item.color.toString().slice(0, -offset) + ', 0.1)'
            } else {
              if (item.color.toString().includes(', 0.1')) {
                item.color = item.color.toString().slice(0, -6) + ')'
              }
            }
          })
        }
      } else {
        datasets.forEach((item: ChartValueItem) => {
          if (!item?.color) return
          if (item.color.toString().includes(', 0.1')) {
            item.color = item.color.toString().slice(0, -6) + ')'
          }
        })
      }
    }
  }
  chartAPI?.update()
}

export const alignScales = (
  currentChart: ChartWithValues,
  currentChartList: ChartWithValues[],
  // TODO should these be dashboardControls + comparativeIndex?
  dashboardControls: DashboardControlsConfig[],
  comparativeIndex: number,
  disableGrouping: boolean,
): ChartWithValues | undefined => {
  if (currentChartList.some((item: ChartWithValues) => !item?.values)) {
    return currentChart
  }

  const currentChartCopy = JSON.parse(JSON.stringify(currentChart))

  if (currentChartCopy.chart_type === ChartTypes.BAR) {
    const minValue = Math.min(
      ...currentChartList.map((item: ChartWithValues) => item.min_value ?? 0),
    )
    const maxValue = Math.max(
      ...currentChartList.map((item: ChartWithValues) => item.max_value ?? 0),
    )

    return {
      ...currentChartCopy,
      min_value: minValue,
      max_value: maxValue,
    }
  } else if (currentChartCopy.chart_type === ChartTypes.BUBBLE) {
    currentChartCopy.shaded_zones?.forEach(
      (item: ShadedZone) => (item.visible = true),
    )

    const currentValuesList = currentChartList.map(
      (item: ChartWithValues, index: number) => {
        return getGroupedChartValues2(
          item,
          dashboardControls[index]?.expandedCategories ?? [],
          dashboardControls[0]?.tier1 ?? '',
          dashboardControls[index]?.showCategories,
          disableGrouping,
        )
      },
    )

    const xValues = currentValuesList
      ?.flat()
      ?.map((element: ChartValueItem) => Number(element[currentChart.x_key]))
      .flat()
    const yValues = currentValuesList
      ?.flat()
      ?.map((element: ChartValueItem) => Number(element[currentChart.y_key]))
      .flat()

    const minXValue: number | undefined = Math.min(...xValues)
    const maxXValue: number | undefined = Math.max(...xValues)
    const minYValue: number | undefined = Math.min(...yValues)
    const maxYValue: number | undefined = Math.max(...yValues)

    const xOffset = (maxXValue - minXValue) / 4
    const yOffset = (maxYValue - minYValue) / 4

    // todo i hate this
    let finalMinXValue =
      minXValue - xOffset === maxXValue + xOffset
        ? undefined
        : minXValue - xOffset
    let finalMaxXValue =
      minXValue - xOffset === maxXValue + xOffset
        ? undefined
        : maxXValue + xOffset

    let finalMinYValue =
      minYValue - yOffset === maxYValue + yOffset
        ? undefined
        : minYValue - yOffset
    let finalMaxYValue =
      minYValue - yOffset === maxYValue + yOffset
        ? undefined
        : maxYValue + yOffset

    if (
      currentChartCopy.x_key === 'rating' ||
      currentChartCopy.x_key === 'volume'
    ) {
      finalMinXValue = finalMinXValue && finalMinXValue < 0 ? 0 : finalMinXValue
    }
    if (currentChartCopy.x_key === 'sentiment') {
      finalMinXValue =
        !finalMinXValue || (finalMinXValue && finalMinXValue) < -1
          ? -1
          : finalMinXValue
      finalMaxXValue =
        !finalMaxXValue || (finalMaxXValue && finalMaxXValue) > 1
          ? 1
          : finalMaxXValue
    }
    if (
      currentChartCopy.y_key === 'rating' ||
      currentChartCopy.y_key === 'volume'
    ) {
      finalMinYValue = finalMinYValue && finalMinYValue < 0 ? 0 : finalMinYValue
    }
    if (currentChartCopy.y_key === 'rating') {
      finalMaxYValue =
        !finalMaxYValue || (finalMaxYValue && finalMaxYValue > 5)
          ? 5
          : finalMaxYValue
    }

    return {
      ...currentChartCopy,
      min_x: finalMinXValue,
      max_x: finalMaxXValue,
      min_y: finalMinYValue,
      max_y: finalMaxYValue,
    }
  } else if (currentChart.chart_type === ChartTypes.LINE) {
    // TODO - do we need to pull the expandedcategories per list index?

    const currentValuesList = currentChartList.map(
      (item: ChartWithValues, index: number) => {
        return getGroupedChartValues2(
          item,
          dashboardControls[index]?.expandedCategories ?? [],
          dashboardControls[0]?.tier1 ?? '',
          dashboardControls[index]?.showCategories,
          disableGrouping,
        )
      },
    )

    const currentChartValues = getGroupedChartValues2(
      currentChart,
      dashboardControls[comparativeIndex]?.expandedCategories ?? [],
      dashboardControls[0]?.tier1 ?? '',
      dashboardControls[comparativeIndex]?.showCategories,
      disableGrouping,
    )

    interface XValueItem {
      label: string | number
      value: number
    }

    const xValues: XValueItem[] = currentValuesList
      ?.flat()
      ?.map((element: ChartValueItem) => {
        return {
          label: element[currentChart.x_key] as string | number,
          value: element.x_value_time as number,
        }
      })
      .flat()

    const uniqueXValues = xValues
      .filter(
        (a: XValueItem, i: number) =>
          xValues.findIndex((s: XValueItem) => a.label === s.label) === i,
      )
      .sort((a: XValueItem, b: XValueItem) => a.value - b.value)
      .map((item: XValueItem) => item.label)
    const yValues = currentValuesList
      ?.flat()
      ?.map((element: ChartValueItem) => element[currentChart.y_key] as number)
      .flat()
    const newValues: ChartValueItem[] = []
    uniqueXValues.forEach((item: string | number) => {
      const foundElements = currentChartValues.filter(
        (element: ChartValueItem) => element.x_value === item,
      )
      if (foundElements.length) {
        newValues.push(...foundElements)
      } else {
        newValues.push({
          category: '',
          x_value: item,
          color: 'rgb(0, 0, 0)',
        })
      }
    })
    const minYValue = Math.min(...yValues)
    const maxYValue = Math.max(...yValues)

    const zValues = currentValuesList
      ?.flat()
      ?.map((element: ChartValueItem) =>
        Number(element[currentChart.z_key ?? '']),
      )
      .flat()

    const minZValue = Math.min(...zValues)
    const maxZValue = Math.max(...zValues)

    return {
      ...currentChart,
      values: newValues,
      min_y: minYValue,
      max_y: maxYValue,
      min_z: minZValue,
      max_z: maxZValue,
    }
  } else if (currentChart.chart_type === ChartTypes.STACK_BAR) {
    const currentValuesList = currentChartList.map(
      (item: ChartWithValues, index: number) => {
        return getGroupedChartValues2(
          item,
          dashboardControls[index]?.expandedCategories ?? [],
          dashboardControls[0]?.tier1 ?? '',
          dashboardControls[index]?.showCategories,
          disableGrouping,
        )
      },
    )

    const maxValue = Math.max(
      ...(currentValuesList?.flat()?.map((element: ChartValueItem) => {
        let volume = 0
        element?.stacked_categories?.forEach((category: StackedCategory) => {
          volume += Number(category['volume'] ?? 0)
        })
        return volume
      }) ?? []),
    )

    return {
      ...currentChart,
      max_value: maxValue,
    }
  }
  return undefined
}

export const alignOrder = (
  currentChart: ChartWithValues,
  currentChartList: ChartWithValues[],
  _comparativeIndex: number,
  expandedCategories: string[],
  order: string,
  sortedByCategory: boolean,
  showCategories: boolean | undefined,
  tier1: string,
  disableBrandGrouping: boolean,
  dashboardControls: DashboardControlsConfig[],
): ChartWithValues => {
  if (currentChart.chart_type === 'table_charts') {
    // DO NOTHING
    // Aligning functionality was written into ../features/table-chart.tsx
    return currentChart
  } else {
    const currentValues = Array.isArray(currentChart.values)
      ? currentChart.values
      : []
    if (!currentValues.length) {
      return currentChart
    }
    const curValues = getChartValues(
      currentChart,
      expandedCategories ?? [],
      sortedByCategory,
      order ?? 'desc',
      tier1,
      showCategories,
      disableBrandGrouping,
    )
    if (
      !currentChartList ||
      !currentChartList.length ||
      currentChartList.includes(undefined as unknown as ChartWithValues)
    )
      return { ...currentChart, values: curValues }

    const yValues = currentChartList
      .map((chart: ChartWithValues, index: number) => {
        return getChartValues(
          chart,
          dashboardControls[index]?.expandedCategories ?? [],
          !!dashboardControls[index]?.sortedByCategory,
          dashboardControls[index]?.order ?? 'desc',
          dashboardControls[0]?.tier1 ?? '',
          dashboardControls[index]?.showCategories,
          disableBrandGrouping,
        )
      })
      .flat()

    if (yValues.includes(null as unknown as ChartValueItem)) return currentChart

    const comparativeField =
      yValues.length && yValues[0]?.category ? 'category' : 'group'
    const uniqueYValues = yValues.filter(
      (a: ChartValueItem, i: number) =>
        yValues.findIndex(
          (s: ChartValueItem) => a[comparativeField] === s[comparativeField],
        ) === i,
    )

    const newValues = uniqueYValues.map((item: ChartValueItem) => {
      const foundItem = curValues?.find(
        (element: ChartValueItem) =>
          element[comparativeField] === item[comparativeField] &&
          !element.hidden,
      )

      return foundItem
        ? { ...foundItem, hidden: false }
        : { ...item, hidden: true }
    })

    return {
      ...currentChart,
      values: newValues,
    }
  }
}

export function sortLegend(legend: ChartLegend[], type: string): ChartLegend[] {
  return type === 'asc'
    ? legend.sort((a: ChartLegend, b: ChartLegend) =>
        a.label?.toString().toLowerCase() > b.label?.toString().toLowerCase()
          ? 1
          : -1,
      )
    : type === 'desc'
      ? legend.sort((a: ChartLegend, b: ChartLegend) =>
          a.label?.toString().toLowerCase() < b.label?.toString().toLowerCase()
            ? 1
            : -1,
        )
      : legend
}

export function setDefaultFontSize(size: string | undefined): void {
  if (size) {
    ;(Chart.defaults.font as Record<string, unknown>)['size'] = +fontSizeDict[
      size
    ]!.slice(0, -2)
  }
}

const isDST = (d: Date): boolean => {
  const jan = new Date(d.getFullYear(), 0, 1).getTimezoneOffset()
  const jul = new Date(d.getFullYear(), 6, 1).getTimezoneOffset()
  return Math.max(jan, jul) !== d.getTimezoneOffset()
}
export const getAdjustedDateRangeString = (
  range:
    | {
        min: number
        max: number
      }
    | undefined,
  useDash?: boolean,
): string => {
  if (!range) return ''
  return `${new Date(
    range.min * 1000 +
      (new Date().getTimezoneOffset() +
        (isDST(new Date(range.min * 1000)) ? 60 : 0)) *
        60000,
  ).toLocaleDateString()} ${useDash ? '-' : 'to'} ${new Date(
    range.max * 1000,
  ).toLocaleDateString()}`
}

export const fontSizeDict: Record<string, string> = {
  extra_small: '8px',
  small: '10px',
  medium: '14px',
  large: '16px',
  extra_large: '18px',
}
export function splitLabelForChart(label: string, lineLimit: number): string[] {
  const words = label.split(' ')
  const lines = []

  let line = ''
  let currentWordIdx = 0

  while (currentWordIdx < words.length) {
    if (line.length + words[currentWordIdx]!.length < lineLimit) {
      line += `${words[currentWordIdx]} `
      currentWordIdx++

      if (currentWordIdx === words.length) {
        lines.push(line)
      }
    } else {
      if (line.length) {
        lines.push(line)
        line = ''
      }

      if (words[currentWordIdx]!.length >= lineLimit) {
        lines.push(words[currentWordIdx]!.trim())
        currentWordIdx++
      }
    }
  }

  return lines
}
export function splitLabelIntoChunks(
  label: string,
  maxChunkLength: number,
): string | string[] {
  if (label.length < maxChunkLength) return label
  const words = label.split(' ')
  const chunks = []
  let currentChunk = words[0]

  words.slice(1).forEach((word) => {
    if (currentChunk!.length + word.length + 1 <= maxChunkLength) {
      currentChunk += ' ' + word
    } else {
      chunks.push(currentChunk)
      currentChunk = word
    }
  })

  // Push the last chunk
  chunks.push(currentChunk)

  return chunks as string[]
}
const excludedColumns: string[] = [
  'color',
  'type_',
  'is_competitor',
  'x_value_time',
  'z_value_color',
]

export async function downloadChartAsCSV(
  chart: UnionChart,
  chartValues: ChartValueItem[],
  tier1: string,
  themeDriversExtension?: () => Promise<ThemeDrivers>,
): Promise<boolean | undefined> {
  if (!('values' in chart)) {
    return
  }

  let categoryKey = (chart as BubbleChart).legend_title || DEFAULT_CATEGORY_KEY
  if (
    chart.chart_type === ChartTypes.LINE ||
    chart.chart_type === ChartTypes.BUBBLE ||
    chart.chart_type === ChartTypes.TABLE
  ) {
    categoryKey = tier1
  }
  if (chart.chart_type === ChartTypes.MATRIX) {
    categoryKey = 'Category'
  }

  if (
    chart.chart_type === ChartTypes.BAR ||
    chart.chart_type === ChartTypes.STACK_BAR
  ) {
    categoryKey = (chart as BarChart).x_title
  }

  let values: ChartValueItem[] = chartValues

  if (!!themeDriversExtension && values.length >= DRIVER_EXPORT_LIMIT) {
    return false
  }

  if (chart.chart_type === ChartTypes.STACK_BAR) {
    values = chartValues
      .map((el) => {
        return el.stacked_categories?.map((category: StackedCategory) => {
          const newObj: Record<string, string | number> = {}
          newObj[categoryKey] = el.category
          newObj[
            pluralize(
              (chart as StackBarChart).chart_id.split('_')[
                chart.chart_id.split('_').length - 1
              ]!,
              1,
            )
          ] = category.category
          Object.keys(category).forEach((key) => {
            if (key === 'stacked_categories') {
              const stackedObj = el[key] as unknown as Record<
                string,
                string | number
              >
              if (stackedObj && typeof stackedObj === 'object') {
                Object.keys(stackedObj).forEach((hoverKey) => {
                  newObj[hoverKey] = stackedObj[hoverKey]!
                })
              }
            } else {
              if (key !== 'category') {
                newObj[key] = category[key]!
              }
            }
          })
          return newObj
        })
      })
      .flat()
      .filter(Boolean) as ChartValueItem[]
  }

  const cwv = chart as unknown as ChartWithValues
  values = values.map((el) => {
    const obj: Record<string, string | number | boolean | null> = {}
    Object.keys(el).forEach((key) => {
      if (key === 'hover') {
        const hoverObj = el.hover
        if (hoverObj && typeof hoverObj === 'object') {
          Object.keys(hoverObj).forEach((hoverKey) => {
            obj[hoverKey] = hoverObj[hoverKey]!
          })
        }
      } else if (
        !excludedColumns.includes(key) &&
        el[key] !== undefined &&
        el[key] !== null
      ) {
        const val = el[key]
        if (key === 'category') {
          obj[categoryKey] = val as string
        } else if (key === 'x_value') {
          obj[cwv.x_title ?? 'x_value'] = val as string | number
        } else if (key === 'y_value') {
          obj[cwv.y_title ?? 'y_value'] = val as string | number
        } else if (key === 'z_value') {
          obj[cwv.z_title ?? 'z_value'] = val as string | number
        } else {
          if (
            typeof val === 'string' ||
            typeof val === 'number' ||
            typeof val === 'boolean' ||
            val === null
          ) {
            obj[key] = val
          }
        }
      }
    })
    return obj
  }) as ChartValueItem[]

  if (themeDriversExtension) {
    const drivers = await themeDriversExtension()
    values = values.map((el) => {
      let driverInfo = {}
      const catKey = String(el[categoryKey] ?? '')
      if (drivers[catKey]) {
        drivers[catKey].overall_themes?.forEach(
          (
            driver: {
              theme: string
              avg_sentiment: number
              mentions_count: number
              mentions_percent: number
            },
            index: number,
          ) => {
            driverInfo = Object.assign({}, driverInfo, {
              [`theme_${index + 1}`]: driver.theme,
              [`theme_${index + 1}_sentiment`]: driver.avg_sentiment,
              [`theme_${index + 1}_sentences`]: driver.mentions_count,
              [`theme_${index + 1}_percent_volume`]:
                driver.mentions_percent + '%',
            })
          },
        )
      }
      return { ...el, ...driverInfo }
    })
  }

  let csvContent = ''
  csvContent +=
    Object.keys(values[0]!)
      .sort((a, b) => {
        if (a === categoryKey) {
          return -1
        } else if (b === categoryKey) {
          return 1
        }
        return 0
      })
      .map(
        (el) =>
          `"${el.includes('#') || el.includes('%') ? el : _.startCase(el)}"`,
      )
      .join(',') + '\n'
  csvContent += values
    .map((el) => {
      return Object.keys(el)
        .sort((a, b) => {
          if (a === categoryKey) {
            return -1
          } else if (b === categoryKey) {
            return 1
          }
          return 0
        })
        .map((key) =>
          !(typeof el[key] === 'object') ? `"${el[key]}"` : 'object',
        )
        .join(',')
    })
    .join('\n')

  // Step 3: Create a Blob
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })

  // Step 4: Create a Download Link
  const link = document.createElement('a')
  link.setAttribute('href', URL.createObjectURL(blob))
  link.setAttribute('download', `${chart.title}.csv`)
  document.body.appendChild(link) // Required for Firefox

  // Step 5: Trigger the Download
  link.click()

  // Step 6: Cleanup
  document.body.removeChild(link)

  toast.success('CSV Downloaded')
  return true
}

export const onCopyChart = async (
  wrapperRef: React.RefObject<HTMLDivElement | HTMLCanvasElement>,
  chartWrapperRef: React.RefObject<HTMLDivElement>,
  title?: string,
  isPrint?: boolean,
  contentType?: string,
  options?: {
    onclone?: (clonedDoc: Document, clonedElement: HTMLElement) => void
  },
): Promise<true | undefined> => {
  if (wrapperRef.current) {
    const parentElement = wrapperRef.current.parentElement
    if (parentElement) {
      if (chartWrapperRef.current && wrapperRef.current) {
        const html2canvas = await loadHtml2Canvas()
        const canvas = await html2canvas(wrapperRef.current, {
          scrollY: -window.scrollY,
          scale: 2,
          ...(options?.onclone ? { onclone: options.onclone } : {}),
        })

        const blob = await new Promise<Blob | null>((resolve) =>
          canvas.toBlob(resolve),
        )

        if (isPrint) {
          if (blob) {
            const link = document.createElement('a')
            link.href = URL.createObjectURL(blob)
            link.download = (title ?? 'download') + '.png'
            link.click()
          }
        } else {
          const item = new ClipboardItem({ 'image/png': blob ?? '' })
          await navigator.clipboard.write([item])
        }

        toast.success(
          isPrint
            ? `${contentType ?? 'Chart'} Downloaded`
            : `${contentType ?? 'Chart'} copied to clipboard`,
        )

        return true
      }
    }
  }
  return undefined
}

export const getChartValues = (
  chart: ChartWithValues,
  expandedCategories: string[],
  sortedByCategory: boolean,
  order: string,
  tier1: string,
  showCategories?: boolean,
  disableGrouping?: boolean,
): ChartValueItem[] => {
  const type1Charts: string[] = [
    ChartTypes.BAR,
    ChartTypes.MATRIX,
    ChartTypes.STACK_BAR,
  ]
  if (type1Charts.includes(chart?.chart_type)) {
    return getGroupedChartValues(
      chart,
      expandedCategories,
      sortedByCategory,
      tier1,
      order,
      showCategories,
      disableGrouping,
    )
  } else {
    return getGroupedChartValues2(
      chart,
      expandedCategories,
      tier1,
      showCategories,
      disableGrouping,
    )
  }
}

// get values for Bar, Stack Bar, Matrix
export const getGroupedChartValues = (
  chart: ChartWithValues,
  expandedCategories: string[],
  sortedByCategory: boolean,
  tier1: string,
  order: string,
  showCategories?: boolean,
  disableGrouping?: boolean,
): ChartValueItem[] => {
  if (!chart?.values) return []

  let values = chart.values

  if (!values) return values
  // if disable and chart is brand lens

  // different default setting for theme vs product and brand
  // Matrix only ever has Themes grouped, but can be viewed in Brand/Product lens,
  // so we treat all matrix as if tier1 = Theme
  const reactiveShowMyBrands =
    chart.category_mapping &&
    (showCategories === undefined
      ? tier1 === 'Theme' || chart.chart_type === ChartTypes.MATRIX
      : showCategories)
  if (disableGrouping || !reactiveShowMyBrands) {
    if (order === 'asc') {
      values = [...values].reverse()
    }
    return values
  }

  const includeParentCategories = !(
    chart.chart_type === ChartTypes.BAR ||
    chart.chart_type === ChartTypes.STACK_BAR ||
    chart.chart_type === ChartTypes.MATRIX
  )

  values = chart.parent_values ?? []

  // janky workaround for matrix % Volume by Theme
  let adjustedParentValues = values
  if (chart.chart_type === ChartTypes.MATRIX && chart.parent_values) {
    adjustedParentValues = chart.parent_values.map((el: ChartValueItem) => {
      const topTheme = el.category.split(' - ')[0]
      const matchingParents = Object.keys(chart.category_mapping!).filter(
        (key) => topTheme === key.split(' - ')[0],
      )
      let maxSubthemes = 1
      if (matchingParents) {
        matchingParents.forEach((parent) => {
          maxSubthemes = Math.max(
            maxSubthemes,
            chart.category_mapping![parent]?.length ?? 0,
          )
        })
      }
      return {
        ...el,
        hover: {
          ...(el.hover ?? {}),
          '% Volume by Theme':
            Number(el.hover?.['% Volume by Theme'] ?? 0) / maxSubthemes,
        },
      }
    })
    values = adjustedParentValues
  }

  if (expandedCategories?.length && chart.category_mapping) {
    let expandedValues: ChartValueItem[] = []
    let parentValues = adjustedParentValues ?? []
    expandedCategories.forEach((category) => {
      if (chart.chart_type === ChartTypes.MATRIX) {
        const theme = category
        expandedValues = expandedValues.concat(
          chart.values.filter(
            (el: ChartValueItem) =>
              el.category.split(' - ')[0]!.split(': ')[0]! === theme,
          ),
        )
        parentValues = parentValues.filter(
          (el: ChartValueItem) => !el.category.includes(category),
        )
      } else {
        const childValues = chart.category_mapping?.[category]
        if (childValues) {
          expandedValues = expandedValues.concat(
            chart.values.filter((item: ChartValueItem) =>
              childValues.includes(item.category),
            ),
          )
          if (!includeParentCategories) {
            parentValues = parentValues.filter(
              (el: ChartValueItem) => el.category !== category,
            )
          }
        }
      }
    })
    values = [...expandedValues, ...parentValues]
  }

  values = [...values].sort((a, b) => {
    const sortField = a.sentiment ? 'sentiment' : a.rating ? 'rating' : 'volume'
    return Number(b[sortField] ?? 0) - Number(a[sortField] ?? 0)
  })

  if (chart.chart_type === ChartTypes.TABLE) {
    // do nothing
  } else if (sortedByCategory) {
    values = [...values].sort((a, b) => {
      const sortField = a.category ? 'category' : 'group'
      return String(a[sortField] ?? '').toLowerCase() >
        String(b[sortField] ?? '').toLowerCase()
        ? 1
        : String(a[sortField] ?? '').toLowerCase() <
            String(b[sortField] ?? '').toLowerCase()
          ? -1
          : 0
    })
  } else {
    values = [...values].sort((a: ChartValueItem, b: ChartValueItem) => {
      const sortField = a.sentiment
        ? 'sentiment'
        : a.rating
          ? 'rating'
          : 'volume'
      if (sortField === 'volume') {
        return (
          (b.stacked_categories?.reduce(
            (acc: number, el: StackedCategory) =>
              acc + Number(el['volume'] ?? 0),
            0,
          ) ?? 0) -
          (a.stacked_categories?.reduce(
            (acc: number, el: StackedCategory) =>
              acc + Number(el['volume'] ?? 0),
            0,
          ) ?? 0)
        )
      }

      return ((b[sortField] as number) ?? 0) - ((a[sortField] as number) ?? 0)
    })
  }

  if (order === 'asc') {
    values = [...values].reverse()
  }
  return values
}

export const getOpenChildLabels = (
  currentChart: ChartWithValues | ValuesChart,
  expandedCategories: string[],
): string[] => {
  // for each expanded category, push the child labels into a list
  let openChildLabels: string[] = []
  const catMapping = (currentChart as ChartWithValues).category_mapping
  if (catMapping && expandedCategories?.length) {
    expandedCategories?.forEach((category) => {
      openChildLabels = openChildLabels.concat(catMapping[category] ?? [])
      openChildLabels = openChildLabels.concat(
        catMapping[category]?.map((item) => item + ' Volume') ?? [],
      )
    })
  }
  return openChildLabels
}

// get values for not (Bar, Stack Bar, Matrix)
// type return is the values type for each chart type
export const getGroupedChartValues2 = (
  currentChart: ChartWithValues | ValuesChart,
  expandedCategories: string[],
  tier1: string,
  showCategories?: boolean,
  disableGrouping?: boolean,
): ChartValueItem[] => {
  const cwv = currentChart as ChartWithValues
  if (!cwv.values) return []
  const openChildLabels = getOpenChildLabels(currentChart, expandedCategories)

  const childValues = (cwv.values as ChartValueItem[]).filter(
    (el: ChartValueItem) => openChildLabels.includes(el.category),
  )

  const reactiveShowMyBrands =
    cwv.category_mapping &&
    (showCategories === undefined ? tier1 === 'Theme' : showCategories)

  let values: ChartValueItem[] =
    cwv.parent_values && reactiveShowMyBrands
      ? [...childValues, ...((cwv.parent_values ?? []) as ChartValueItem[])]
      : (cwv.values as ChartValueItem[])

  if (disableGrouping) {
    values = cwv.values as ChartValueItem[]
  }
  return values
}

interface RowWithCategory {
  category: string
  subRows?: RowWithCategory[]
}

export function alignRows(
  firstList: RowWithCategory[],
  secondList: RowWithCategory[],
): RowWithCategory[] {
  const firstListMap = new Map<string, RowWithCategory>()
  firstList.forEach((row: RowWithCategory) => {
    firstListMap.set(row.category, row)
  })

  secondList.sort((a: RowWithCategory, b: RowWithCategory) => {
    const aRow = firstListMap.get(a.category)
    const bRow = firstListMap.get(b.category)

    if (!aRow && !bRow) return 0
    if (!aRow) return 1
    if (!bRow) return -1

    return (
      Array.from(firstListMap.values()).indexOf(aRow) -
      Array.from(firstListMap.values()).indexOf(bRow)
    )
  })

  // sort subRows
  secondList.forEach((el: RowWithCategory) => {
    if (el.subRows?.length) {
      el.subRows = alignRows(
        firstListMap.get(el.category)?.subRows ?? [],
        el.subRows,
      )
    }
  })

  return secondList
}

export const interpolateSentimentColor = (
  value: number,
  isUnfocused: boolean,
): string => {
  const colorYellow = { r: 240, g: 228, b: 66 }
  const colorGreen = { r: 0, g: 158, b: 115 }
  const colorRed = { r: 213, g: 94, b: 0 }
  value = Math.max(-1, Math.min(value, 1))

  let r: number, g: number, b: number

  if (value < 0) {
    // Interpolate between red and yellow
    r = colorRed.r + (colorYellow.r - colorRed.r) * (value + 1)
    g = colorRed.g + (colorYellow.g - colorRed.g) * (value + 1)
    b = colorRed.b + (colorYellow.b - colorRed.b) * (value + 1)
  } else {
    // Interpolate between yellow and green
    r = colorYellow.r + (colorGreen.r - colorYellow.r) * value
    g = colorYellow.g + (colorGreen.g - colorYellow.g) * value
    b = colorYellow.b + (colorGreen.b - colorYellow.b) * value
  }

  return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}${
    isUnfocused ? ', 0.1' : ''
  })`
}

export const interpolateRatingColor = (
  value: number,
  isUnfocused: boolean,
): string => {
  const colorGreen = { r: 0, g: 158, b: 115 }
  const colorLightGreen = { r: 127, g: 198, b: 92 }
  const colorYellow = { r: 240, g: 228, b: 66 }
  const colorOrange = { r: 233, g: 162, b: 7 }
  const colorRed = { r: 213, g: 94, b: 0 }
  value = Math.max(0, Math.min(value, 5))

  let r: number, g: number, b: number

  let interpolationFactor: number

  if (value < 1.5) {
    // Interpolate between colorRed and colorOrange
    interpolationFactor = value / 1.5
    r = colorRed.r + (colorOrange.r - colorRed.r) * interpolationFactor
    g = colorRed.g + (colorOrange.g - colorRed.g) * interpolationFactor
    b = colorRed.b + (colorOrange.b - colorRed.b) * interpolationFactor
  } else if (value < 2.5) {
    // Interpolate between colorOrange and colorYellow
    interpolationFactor = value - 1.5
    r = colorOrange.r + (colorYellow.r - colorOrange.r) * interpolationFactor
    g = colorOrange.g + (colorYellow.g - colorOrange.g) * interpolationFactor
    b = colorOrange.b + (colorYellow.b - colorOrange.b) * interpolationFactor
  } else if (value < 3.5) {
    // Interpolate between colorYellow and colorLightGreen
    interpolationFactor = value - 2.5
    r =
      colorYellow.r + (colorLightGreen.r - colorYellow.r) * interpolationFactor
    g =
      colorYellow.g + (colorLightGreen.g - colorYellow.g) * interpolationFactor
    b =
      colorYellow.b + (colorLightGreen.b - colorYellow.b) * interpolationFactor
  } else if (value < 5) {
    // Interpolate between colorLightGreen and colorGreen
    interpolationFactor = (value - 3.5) / 1.5
    r =
      colorLightGreen.r +
      (colorGreen.r - colorLightGreen.r) * interpolationFactor
    g =
      colorLightGreen.g +
      (colorGreen.g - colorLightGreen.g) * interpolationFactor
    b =
      colorLightGreen.b +
      (colorGreen.b - colorLightGreen.b) * interpolationFactor
  } else {
    // Value is at or above 4.5, use color5
    return `rgb(${colorGreen.r}, ${colorGreen.g}, ${colorGreen.b})`
  }

  return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}${
    isUnfocused ? ', 0.1' : ''
  })`
}

export const filterExpandedCategories = (
  currentChart: UnionChart,
  expandedCategories: string[],
): string[] => {
  if (!currentChart?.category_mapping) return expandedCategories
  if ('category_mapping' in currentChart) {
    const expanded = new Set(expandedCategories)

    let filtered = Object.keys(currentChart.category_mapping).filter(
      (el) => expanded.has(el) || expanded.has(el.split(': ')[1]!),
    )
    if (matrixChartIds.includes(currentChart.chart_id)) {
      const matrixKeys = Object.keys(currentChart.category_mapping).map((el) =>
        el.split(' - ')[0]!.trim(),
      )
      filtered = _.uniq(
        matrixKeys.filter(
          (el) => expanded.has(el) || expanded.has(el.split(': ')[1]!),
        ),
      )
    }
    return filtered
  }
  return expandedCategories
}
